import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import LoginIcon from '@mui/icons-material/Login';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import Header from '../components/Header';
import SaleCard from '../components/SaleCard';
import { useSales } from '../hooks/useSales';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { Sale } from '../types';

interface UnavailableSale {
  saleId: string;
  title: string;
}

const FavoritesScreen: React.FC = () => {
  const { favorites, removeFavorite } = useApp();
  const { isAuthenticated } = useAuth();
  
  // Fetch all active sales - remove city filter so we can match ALL favorite IDs
  const { data: allSales, isLoading } = useSales({ enabled: true });
  
  const [unavailableSales, setUnavailableSales] = useState<UnavailableSale[]>([]);

  // Separate available and unavailable favorites
  const { availableFavorites, unavailableFavorites } = useMemo(() => {
    console.log('=== FavoritesScreen Debug ===');
    console.log('favorites array:', favorites);
    console.log('allSales loaded:', !!allSales, allSales?.length);
    
    if (!allSales || allSales.length === 0) {
      return { 
        availableFavorites: [], 
        unavailableFavorites: favorites.map(favId => ({ saleId: favId, title: 'Loading...' })) 
      };
    }
    
    const available: Sale[] = [];
    const unavailable: UnavailableSale[] = [];
    
    favorites.forEach(favId => {
      const sale = allSales.find(s => s.id === favId);
      if (sale) {
        available.push(sale);
      } else {
        unavailable.push({ saleId: favId, title: 'Removed sale' });
      }
    });
    
    console.log('available count:', available.length);
    console.log('unavailable count:', unavailable.length);
    
    return { availableFavorites: available, unavailableFavorites: unavailable };
  }, [allSales, favorites]);

  // Clear unavailable sales from favorites
  const handleClearUnavailable = () => {
    unavailableFavorites.forEach(sale => {
      removeFavorite(sale.saleId);
    });
    setUnavailableSales([]);
  };

  // Update unavailable sales list when it changes
  useEffect(() => {
    setUnavailableSales(unavailableFavorites);
  }, [unavailableFavorites]);

  // Show loading while fetching sales
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: 'background.default' }}>
        <Header title="Favorites" showBackButton={false} />
        <Box sx={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  const totalFavorites = availableFavorites.length + unavailableFavorites.length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: 'background.default' }}>
      <Header title="Favorites" showBackButton={false} />

      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {/* Login Prompt for Unauthenticated Users */}
        {!isAuthenticated && favorites.length > 0 && (
          <Alert 
            severity="info" 
            icon={<LoginIcon />}
            sx={{ mb: 2 }}
            action={
              <Button 
                color="inherit" 
                size="small"
                onClick={() => window.location.href = '/login'}
              >
                Login
              </Button>
            }
          >
            Login to save your favorites across devices
          </Alert>
        )}

        {/* Main Content */}
        {totalFavorites === 0 ? (
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center', 
              height: '50%',
              textAlign: 'center',
              px: 4,
            }}
          >
            <FavoriteIcon sx={{ fontSize: 64, color: 'grey.300', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No favorites yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Save sales you're interested in by tapping the heart icon
            </Typography>
            {!isAuthenticated && (
              <Button
                variant="outlined"
                startIcon={<LoginIcon />}
                sx={{ mt: 3 }}
                onClick={() => window.location.href = '/login'}
              >
                Login to save favorites
              </Button>
            )}
          </Box>
        ) : (
          <>
            {/* Summary */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {availableFavorites.length} saved sale{availableFavorites.length !== 1 ? 's' : ''}
                {unavailableFavorites.length > 0 && (
                  <Chip 
                    label={`${unavailableFavorites.length} unavailable`} 
                    size="small" 
                    color="warning"
                    sx={{ ml: 1 }}
                  />
                )}
              </Typography>
              
              {unavailableFavorites.length > 0 && (
                <Button
                  size="small"
                  startIcon={<DeleteSweepIcon />}
                  onClick={handleClearUnavailable}
                  color="warning"
                >
                  Clear unavailable
                </Button>
              )}
            </Box>

            {/* Unavailable Sales (grayed out) */}
            {unavailableFavorites.map((unavailable) => (
              <Box key={unavailable.saleId} sx={{ mb: 2 }}>
                <SaleCard 
                  sale={{
                    id: unavailable.saleId,
                    source_url: '',
                    source_website: '',
                    title: 'Sale no longer available',
                    description: null,
                    address: '',
                    city: '',
                    state: '',
                    zip_code: null,
                    timezone: '',
                    location: null,
                    start_date: '',
                    end_date: '',
                    start_time: null,
                    end_time: null,
                    is_archived: true,
                    created_at: '',
                    updated_at: '',
                  }}
                  isFavorite={true}
                  isUnavailable={true}
                  onFavoriteToggle={(id) => removeFavorite(id)}
                />
              </Box>
            ))}

            {/* Available Favorites */}
            {availableFavorites.map((sale) => (
              <Box key={sale.id} sx={{ mb: 2 }}>
                <SaleCard 
                  sale={sale}
                  isFavorite={true}
                  onFavoriteToggle={(id) => removeFavorite(id)}
                />
              </Box>
            ))}
          </>
        )}
      </Box>
    </Box>
  );
};

export default FavoritesScreen;